import React from 'react';
import {
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Icon,
  Collapse,
  Button,
  FormControlLabel,
  Checkbox,
  Chip,
  Card,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Title, Loader, DateWeekRangeSelector } from '../../elements';
import { ApiService } from '../../hooks';
import { notifications, extractApiErrorMessage, formatDate } from '../../utils';
import { DISPLAY_WEIGHT_UNIT, convertWeight, getWeightUnitLabel } from '../../utils';
import { getAnimalTypeName } from '../../utils/animalutils';
import { LoaderSize } from '../../constants/constants';
import { useStyles } from './styles';
import {
  HealthSystemDomainLocationsCommandsLocationHistoryDateGroupViewModel,
  HealthSystemAPIControllersLocationsResponsesLocationHistoryResponse,
  HealthSystemDALModelDoubleWeightWithDate,
  HealthSystemDomainLocationsCommandsAnimalInLocationViewModel
} from '../../api/HealthSystemApi';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme/tokens';

interface LocationHistoryProps {
  locationId: string;
}

interface GroupSummary {
  entryDate: string;
  totalAnimals: number;
  currentAnimals: number;
  exitedAnimals: number;
  avgEntryWeight: number | null;
  avgExitWeight: number | null;
  avgDaysInLocation: number;
}

const LocationHistory: React.FC<LocationHistoryProps> = ({ locationId }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const apiService = new ApiService();
  const navigate = useNavigate();

  const [historyData, setHistoryData] =
    React.useState<HealthSystemAPIControllersLocationsResponsesLocationHistoryResponse | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null>(null);
  const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null>(null);
  const [currentOnly, setCurrentOnly] = React.useState(false);
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

  // Always kilograms - the farm's weight type is deliberately not consulted for display.
  const targetWeightUnit = DISPLAY_WEIGHT_UNIT;
  const weightUnitLabel = getWeightUnitLabel(targetWeightUnit, t);

  const handleRowClick = (animal: HealthSystemDomainLocationsCommandsAnimalInLocationViewModel) => {
    navigate(`/animal/${animal.id}`);
  };

  const calculateGroupSummary = (
    dateGroup: HealthSystemDomainLocationsCommandsLocationHistoryDateGroupViewModel
  ): GroupSummary => {
    const animals = dateGroup.animals;
    const currentCount = animals.filter((a) => a.isCurrentlyInLocation).length;

    const entryWeights = animals
      .map((a) => convertWeight(a.weightAtEntry?.value, a.weightAtEntry?.unit, targetWeightUnit))
      .filter((w) => w !== null) as number[];
    const avgEntryWeight =
      entryWeights.length > 0 ? entryWeights.reduce((sum, w) => sum + w, 0) / entryWeights.length : null;

    const exitedAnimals = animals.filter((a) => !a.isCurrentlyInLocation && a.weightAtExit?.value);
    const exitWeights = exitedAnimals
      .map((a) => convertWeight(a.weightAtExit?.value, a.weightAtExit?.unit, targetWeightUnit))
      .filter((w) => w !== null) as number[];
    const avgExitWeight =
      exitWeights.length > 0 ? exitWeights.reduce((sum, w) => sum + w, 0) / exitWeights.length : null;

    const avgDays = animals.reduce((sum, a) => sum + a.daysInLocation, 0) / animals.length;

    return {
      entryDate: dateGroup.entryDate,
      totalAnimals: animals.length,
      currentAnimals: currentCount,
      exitedAnimals: animals.length - currentCount,
      avgEntryWeight: avgEntryWeight !== null ? Math.round(avgEntryWeight * 100) / 100 : null,
      avgExitWeight: avgExitWeight !== null ? Math.round(avgExitWeight * 100) / 100 : null,
      avgDaysInLocation: Math.round(avgDays)
    };
  };

  const prepareChartData = () => {
    if (!historyData?.dateGroups) return [];

    return historyData.dateGroups.map((group) => {
      const summary = calculateGroupSummary(group);
      return {
        entryDate: formatDate(group.entryDate),
        avgEntryWeight: summary.avgEntryWeight,
        avgExitWeight: summary.avgExitWeight
      };
    });
  };

  const loadLocationHistory = async () => {
    setIsBusy(true);

    const fromDate = selectedDateFrom?.toISOString().split('T')[0];
    const toDate = selectedDateTo?.toISOString().split('T')[0];

    await apiService.api.locations
      .locationsGetLocationHistory({
        id: locationId,
        from: fromDate,
        to: toDate,
        currentOnly: currentOnly
      })
      .then((response) => {
        setHistoryData(response.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setLoaded(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadLocationHistory();
    }
  }, [loaded]);

  const handleDateChange = (fromDate: Date, toDate: Date) => {
    setSelectedDateFrom(fromDate);
    setSelectedDateTo(toDate);
  };

  const handleCurrentOnlyToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentOnly(event.target.checked);
  };

  const handleSearch = () => {
    setLoaded(false);
  };

  const toggleGroup = (entryDate: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(entryDate)) {
      newExpanded.delete(entryDate);
    } else {
      newExpanded.add(entryDate);
    }
    setExpandedGroups(newExpanded);
  };

  const formatWeight = (weight: HealthSystemDALModelDoubleWeightWithDate | null | undefined): string => {
    if (!weight?.value) return '-';
    const converted = convertWeight(weight.value, weight.unit, targetWeightUnit);
    return converted !== null ? `${converted} ${weightUnitLabel}` : '-';
  };

  const groups = historyData?.dateGroups || [];
  const total = groups.length;
  const chartData = prepareChartData();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={1}
            className={classes.header}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Grid item style={{ display: 'flex', alignItems: 'center' }}>
              <Icon color="primary" fontSize="large">
                history
              </Icon>
            </Grid>
            <Grid item style={{ display: 'flex', alignItems: 'center' }}>
              <Title>{t('f-loc-view-history')}</Title>
            </Grid>
            <Grid item style={{ display: 'flex', alignItems: 'center' }}>
              <Chip label={total} color="primary" />
            </Grid>
          </Grid>

          <Grid container spacing={3} alignItems="center" className={classes.filtersBox}>
            <Grid item xs={12} md={7}>
              <DateWeekRangeSelector onChange={handleDateChange} monthsBack={3} allowFuture={false} fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Checkbox checked={currentOnly} onChange={handleCurrentOnlyToggle} color="primary" />}
                label={t('f-loc-hist-active-only')}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={isBusy}
                fullWidth
                size="large"
              >
                <Icon>search</Icon>
                {t('f-search')}
              </Button>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {!isBusy && loaded && (
            <>
              <Grid item xs={12}>
                <Table className={classes.table} size="small">
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t('f-loc-hist-entry-date')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-total-animals')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-current-animals')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-exited-animals')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-avg-entry-weight')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-avg-exit-weight')}</TableCell>
                      <TableCell align="right">{t('f-loc-hist-avg-days')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Chip label={t('f-loc-hist-no-data')} />
                        </TableCell>
                      </TableRow>
                    )}
                    {groups.map((group) => {
                      const summary = calculateGroupSummary(group);
                      const isExpanded = expandedGroups.has(summary.entryDate);

                      return (
                        <React.Fragment key={summary.entryDate}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton onClick={() => toggleGroup(summary.entryDate)} size="small">
                                <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                              </IconButton>
                            </TableCell>
                            <TableCell>{formatDate(summary.entryDate)}</TableCell>
                            <TableCell align="right">{summary.totalAnimals}</TableCell>
                            <TableCell align="right">{summary.currentAnimals}</TableCell>
                            <TableCell align="right">{summary.exitedAnimals}</TableCell>
                            <TableCell align="right">
                              {summary.avgEntryWeight !== null ? `${summary.avgEntryWeight} ${weightUnitLabel}` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {summary.avgExitWeight !== null ? `${summary.avgExitWeight} ${weightUnitLabel}` : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {summary.avgDaysInLocation > 0 ? summary.avgDaysInLocation : '-'}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell colSpan={8} className={classes.collapseCell}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Grid container className={classes.detailsContainer}>
                                  <Grid item xs={12}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>{t('f-loc-hist-animal-number')}</TableCell>
                                          <TableCell>{t('f-loc-hist-animal-type')}</TableCell>
                                          <TableCell>{t('f-loc-hist-birth-date')}</TableCell>
                                          <TableCell>{t('f-loc-hist-exit-date')}</TableCell>
                                          <TableCell align="right">{t('f-loc-hist-days')}</TableCell>
                                          <TableCell align="right">{t('f-loc-hist-entry-weight')}</TableCell>
                                          <TableCell align="right">{t('f-loc-hist-exit-weight')}</TableCell>
                                          <TableCell>{t('f-loc-hist-status')}</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {group.animals.map((animal) => (
                                          <TableRow
                                            key={animal.id}
                                            hover
                                            className={classes.tableRowClickable}
                                            onClick={() => handleRowClick(animal)}
                                          >
                                            <TableCell>{animal.number}</TableCell>
                                            <TableCell>{getAnimalTypeName(animal.animalType, t)}</TableCell>
                                            <TableCell>{formatDate(animal.dateOfBirth)}</TableCell>
                                            <TableCell>{animal.exitedAt ? formatDate(animal.exitedAt) : '-'}</TableCell>
                                            <TableCell align="right">
                                              {animal.daysInLocation > 0 ? animal.daysInLocation : '-'}
                                            </TableCell>
                                            <TableCell align="right">{formatWeight(animal.weightAtEntry)}</TableCell>
                                            <TableCell align="right">{formatWeight(animal.weightAtExit)}</TableCell>
                                            <TableCell>
                                              <Chip
                                                label={
                                                  animal.isCurrentlyInLocation
                                                    ? t('f-loc-hist-status-current')
                                                    : t('f-loc-hist-status-exited')
                                                }
                                                color={animal.isCurrentlyInLocation ? 'primary' : 'default'}
                                                size="small"
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Grid>
                                </Grid>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Grid>

              {chartData.length > 0 && (
                <Grid item xs={12}>
                  <Card className={classes.chartCard}>
                    <Grid container direction="row" alignItems="center" spacing={1} className={classes.chartHeader}>
                      <Grid item>
                        <Icon color="primary">insert_chart</Icon>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6">{t('f-loc-hist-chart-title')}</Typography>
                      </Grid>
                    </Grid>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="entryDate" />
                        <YAxis label={{ value: weightUnitLabel, angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value: any) => (value !== null ? `${value} ${weightUnitLabel}` : '-')} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="avgEntryWeight"
                          stroke={tokens.primary.main}
                          name={t('f-loc-hist-avg-entry-weight')}
                          strokeWidth={2}
                          connectNulls
                        />
                        <Line
                          type="monotone"
                          dataKey="avgExitWeight"
                          stroke="#4caf50"
                          name={t('f-loc-hist-avg-exit-weight')}
                          strokeDasharray="5 5"
                          strokeWidth={2}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                </Grid>
              )}
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export { LocationHistory };
